import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import { useMaintenance } from '../hooks/useMaintenance';
import EmergencyModal from './EmergencyModal';
import * as FiIcons from 'react-icons/fi';

const { 
  FiMenu, FiX, FiHome, FiTool, FiMail, FiUser, FiChevronDown, 
  FiChevronUp, FiLayers, FiShield, FiGrid, FiPaintBucket, 
  FiUsers, FiCamera, FiBookOpen, FiAlertTriangle 
} = FiIcons;

function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [hoveredMenu, setHoveredMenu] = useState(null);
  const [openMobileSubmenu, setOpenMobileSubmenu] = useState(null);
  const [isEmergencyOpen, setIsEmergencyOpen] = useState(false);
  
  const { isBypassActive, clearBypass } = useMaintenance();
  const location = useLocation();

  const navigation = [
    { name: 'Startseite', href: '/', icon: FiHome },
    { 
      name: 'Über uns', 
      href: '/ueber', 
      icon: FiUsers,
      hasSubmenu: true,
      submenu: [
        { name: 'Team & Werte', href: '/ueber', icon: FiUsers, description: 'Lernen Sie uns kennen.' },
        { name: 'Portfolio', href: '/portfolio', icon: FiCamera, description: 'Unsere Referenzen.' }
      ]
    },
    { 
      name: 'Dienstleistungen', 
      href: '/services', 
      icon: FiTool,
      hasSubmenu: true,
      submenu: [
        { name: 'Trocken- und Akustikbau', href: '/trockenbau', icon: FiLayers, description: 'Professionelle Trockenbauarbeiten.' },
        { name: 'Dämmtechnik', href: '/daemmtechnik', icon: FiShield, description: 'Energieeffizienz.' },
        { name: 'Innentürmontage', href: '/innentuer', icon: FiHome, description: 'Montage von Innentüren.' },
        { name: 'Bodenbelagsarbeiten', href: '/bodenbelag', icon: FiGrid, description: 'Verlegung von Bodenbelägen.' },
        { name: 'Malerarbeiten', href: '/maler', icon: FiPaintBucket, description: 'Maler- und Beschichtungsarbeiten.' },
        { name: 'PU-Designböden', href: '/designboden', icon: FiTool, description: 'Moderne Designböden mit 3D-Optik.' },
      ]
    },
    { name: 'Blog', href: '/blog', icon: FiBookOpen },
    { name: 'Kontakt', href: '/kontakt', icon: FiMail }
  ];

  const isActive = (item) => {
    if (item.href === '/services') {
      return location.pathname === '/services' || 
             ['/trockenbau', '/daemmtechnik', '/innentuer', '/bodenbelag', '/maler', '/designboden'].includes(location.pathname);
    }
    if (item.href === '/ueber') {
      return location.pathname === '/ueber' || location.pathname === '/portfolio';
    }
    if (item.href === '/blog') {
      return location.pathname.startsWith('/blog');
    }
    return location.pathname === item.href;
  };

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 0);
    window.addEventListener('scroll', handleScroll);
    
    if (!document.querySelector('script[src="https://elfsightcdn.com/platform.js"]')) {
      const script = document.createElement('script');
      script.src = 'https://elfsightcdn.com/platform.js';
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
    }

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <>
      <EmergencyModal isOpen={isEmergencyOpen} onClose={() => setIsEmergencyOpen(false)} />

      {isBypassActive && (
        <div className="fixed top-20 right-4 z-[60] bg-yellow-500 text-yellow-900 px-4 py-2 rounded-lg shadow-lg flex items-center space-x-2">
          <SafeIcon icon={FiShield} className="w-4 h-4" />
          <span className="text-sm font-medium">Bypass aktiv</span>
          <button onClick={clearBypass} className="ml-2 text-yellow-700 hover:text-yellow-900">
            <SafeIcon icon={FiX} className="w-4 h-4" />
          </button>
        </div>
      )}

      <header 
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled ? 'bg-white/95 backdrop-blur-md shadow-lg' : 'bg-white shadow-lg'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            {/* Logo */}
            <Link to="/" className="flex items-center space-x-4">
              <img 
                src="https://wasacon.de/logo_mk_ausbau.png" 
                alt="MK-AUSBAU Logo" 
                className="h-12 w-auto object-contain" 
              />
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden xl:flex items-center space-x-4">
              <nav className="flex items-center space-x-2">
                {navigation.map((item) => (
                  <div 
                    key={item.name}
                    className="relative"
                    onMouseEnter={() => { if (item.hasSubmenu) setHoveredMenu(item.name); }}
                    onMouseLeave={() => { setHoveredMenu(null); }}
                  >
                    <Link
                      to={item.href}
                      className={`flex items-center justify-center space-x-1 min-w-[120px] h-12 px-4 py-3 rounded-sm text-base font-semibold transition-all duration-300 ${
                        isActive(item) 
                          ? 'bg-mk-red-700 text-white shadow-lg' 
                          : 'bg-gray-100 text-gray-700 hover:bg-mk-teal-500 hover:text-white'
                      }`}
                    >
                      <span>{item.name}</span>
                      {item.hasSubmenu && <SafeIcon icon={FiChevronDown} className="w-4 h-4" />}
                    </Link>

                    {/* Desktop Submenu */}
                    <AnimatePresence>
                      {(item.hasSubmenu && hoveredMenu === item.name) && (
                        <motion.div
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: 10 }}
                          className={`absolute top-full left-1/2 transform -translate-x-1/2 mt-2 bg-white rounded-lg shadow-2xl border border-gray-200 p-4 z-50 ${
                            item.name === 'Dienstleistungen' ? 'w-[500px]' : 'w-[300px]'
                          }`}
                        >
                          <div className={`grid gap-4 ${item.name === 'Dienstleistungen' ? 'grid-cols-2' : 'grid-cols-1'}`}>
                            {item.submenu.map((sub) => (
                              <Link 
                                key={sub.name}
                                to={sub.href}
                                className="p-3 rounded-lg hover:bg-mk-teal-50 group border border-transparent hover:border-mk-teal-200"
                              >
                                <div className="flex items-center space-x-3 mb-1">
                                  <SafeIcon icon={sub.icon} className="w-5 h-5 text-mk-teal-500" />
                                  <span className="font-bold text-sm text-gray-900">{sub.name}</span>
                                </div>
                                <p className="text-xs text-gray-600">{sub.description}</p>
                              </Link>
                            ))}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                ))}
              </nav>

              {/* Login Button */}
              <a 
                href="https://kundenportal.ausbau.nrw/authentication/login" 
                target="_blank" 
                rel="noopener noreferrer"
                className="bg-mk-teal-500 hover:bg-mk-teal-600 text-white px-6 py-3 rounded-sm font-semibold shadow-lg flex items-center space-x-2"
              >
                <SafeIcon icon={FiUser} />
                <span className="text-base">Login</span>
              </a>

              {/* Desktop Translator */}
              <div className="ml-2">
                <div className="elfsight-app-04979ed8-d37b-43e0-ae2a-3ec147044342" data-elfsight-app-lazy></div>
              </div>
            </div>

            {/* Mobile Menu Trigger & Translator */}
            <div className="xl:hidden flex items-center gap-3">
              {/* Notfall Button Mobile - Header Compact - Removed here to declutter, available in menu */}
              {/* If you want to keep the small icon in header too, uncomment below. Removing for cleaner look as requested. */}
              {/* <button onClick={() => setIsEmergencyOpen(true)} ... ><SafeIcon icon={FiAlertTriangle}/></button> */}
              
              <div className="elfsight-app-04979ed8-d37b-43e0-ae2a-3ec147044342" data-elfsight-app-lazy></div>
              
              <button 
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="p-2 text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
                aria-label="Menü öffnen"
              >
                <SafeIcon icon={isMenuOpen ? FiX : FiMenu} className="w-8 h-8" />
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Navigation Content */}
        <AnimatePresence>
          {isMenuOpen && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="xl:hidden bg-white border-t overflow-hidden shadow-inner"
            >
              <div className="flex flex-col max-h-[80vh] overflow-y-auto">
                
                {/* Navigation Items */}
                {navigation.map((item) => (
                  <div key={item.name} className="border-b border-gray-100 last:border-0">
                    {item.hasSubmenu ? (
                      <div>
                        <button
                          onClick={() => setOpenMobileSubmenu(openMobileSubmenu === item.name ? null : item.name)}
                          className={`w-full flex items-center justify-between p-4 font-bold text-lg ${
                            openMobileSubmenu === item.name ? 'text-mk-teal-600 bg-mk-teal-50' : 'text-gray-800'
                          }`}
                        >
                          <span className="flex items-center">
                            <SafeIcon icon={item.icon} className="mr-3 text-mk-teal-500" />
                            {item.name}
                          </span>
                          <SafeIcon icon={openMobileSubmenu === item.name ? FiChevronUp : FiChevronDown} className="w-5 h-5" />
                        </button>
                        <AnimatePresence>
                          {openMobileSubmenu === item.name && (
                            <motion.div
                              initial={{ height: 0 }}
                              animate={{ height: 'auto' }}
                              exit={{ height: 0 }}
                              className="bg-gray-50 overflow-hidden"
                            >
                              <div className="p-2 space-y-1">
                                {item.submenu.map((sub) => (
                                  <Link
                                    key={sub.name}
                                    to={sub.href}
                                    onClick={() => setIsMenuOpen(false)}
                                    className="flex items-center space-x-4 p-4 rounded-lg bg-white border border-gray-100"
                                  >
                                    <div className="w-10 h-10 bg-mk-teal-500 rounded-lg flex items-center justify-center">
                                      <SafeIcon icon={sub.icon} className="w-5 h-5 text-white" />
                                    </div>
                                    <div>
                                      <span className="block font-bold text-gray-900 text-sm">{sub.name}</span>
                                      <span className="block text-xs text-gray-500">{sub.description}</span>
                                    </div>
                                  </Link>
                                ))}
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    ) : (
                      <Link
                        to={item.href}
                        onClick={() => setIsMenuOpen(false)}
                        className="flex items-center p-4 font-bold text-lg text-gray-800"
                      >
                        <SafeIcon icon={item.icon} className="mr-3 text-mk-teal-500" />
                        {item.name}
                      </Link>
                    )}
                  </div>
                ))}

                {/* Mobile Login Button */}
                <div className="p-4 border-t border-gray-100">
                  <a 
                    href="https://kundenportal.ausbau.nrw/authentication/login" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="flex items-center justify-center space-x-2 bg-mk-teal-500 text-white p-4 rounded-sm font-bold w-full"
                  >
                    <SafeIcon icon={FiUser} />
                    <span>Kundenlogin</span>
                  </a>
                </div>

                {/* Mobile Notfall Button - Jetzt UNTER dem Login */}
                <div className="p-4 pt-0">
                  <button 
                    onClick={() => { setIsMenuOpen(false); setIsEmergencyOpen(true); }}
                    className="flex items-center justify-center space-x-2 bg-white text-mk-red-700 border border-mk-red-100 p-4 rounded-sm font-bold w-full shadow-sm hover:bg-mk-red-50 transition-colors"
                  >
                    <SafeIcon icon={FiAlertTriangle} />
                    <span>Notfallkontakt öffnen</span>
                  </button>
                </div>

              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </header>
      <div className="h-20"></div>
    </>
  );
}

export default Header;